import { useState, useEffect } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { 
  Save as SaveIcon, 
  Email as EmailIcon,
  Visibility,
  VisibilityOff,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Payment as PaymentIcon,
  Code as ApiIcon,
  Facebook as FacebookIcon,
  FileCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Power as PowerIcon,
  Delete as DeleteIcon,
  Storage as StorageIcon,
} from "@material-ui/icons";
import { 
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Button,
  Collapse,
  LinearProgress,
  Box,
} from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import packageJson from "../../../package.json";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "calc(100vh - 64px)",
    overflowY: "auto",
  },
  header: {
    marginBottom: "24px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  sectionCard: {
    background: "white",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    marginBottom: "24px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "20px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e8ecf1",
  },
  sectionIcon: {
    marginRight: "12px",
    color: "#3b82f6",
    fontSize: "24px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#1a1a1a",
  },
  formGroup: {
    marginBottom: "16px",
  },
  label: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    marginBottom: "6px",
    display: "block",
  },
  helpText: {
    fontSize: "12px",
    color: "#999",
    marginTop: "4px",
  },
  infoBox: {
    background: "#f0f9ff",
    border: "1px solid #bae6fd",
    borderRadius: "8px",
    padding: "16px",
    marginTop: "16px",
  },
  infoBoxText: {
    fontSize: "12px",
    color: "#0369a1",
    lineHeight: "1.6",
  },
  saveButton: {
    marginTop: "16px",
    textTransform: "none",
    fontWeight: 600,
  },
  switchContainer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    background: "#fafbfc",
    borderRadius: "6px",
    border: "1px solid #e8ecf1",
  },
  sectionHeaderClickable: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e8ecf1",
    cursor: "pointer",
    userSelect: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    transition: "opacity 0.2s ease",
    "&:hover": {
      opacity: 0.8,
    },
  },
  expandIcon: {
    transition: "transform 0.3s ease",
  },
  expandIconOpen: {
    transform: "rotate(180deg)",
  },
}));

const SystemSettings = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [version, setVersion] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userCreation, setUserCreation] = useState("disabled");
  const [loadingUserCreation, setLoadingUserCreation] = useState(false);
  const [smtpSettings, setSmtpSettings] = useState({
    smtpHost: "smtp.gmail.com",
    smtpPort: "465",
    smtpUser: "",
    smtpPass: "",
    smtpFrom: "",
    smtpSecure: "true",
  });
  const [mpToken, setMpToken] = useState("");
  const [asaasToken, setAsaasToken] = useState("");
  const [mpConfigured, setMpConfigured] = useState(false);
  const [asaasConfigured, setAsaasConfigured] = useState(false);
  const [uazapiBaseUrl, setUazapiBaseUrl] = useState("https://api.uazapi.com");
  const [uazapiAdminToken, setUazapiAdminToken] = useState("");
  const [uazapiConfigured, setUazapiConfigured] = useState(false);
  const [facebookVerifyToken, setFacebookVerifyToken] = useState("plataformazap");
  const [facebookAppId, setFacebookAppId] = useState("");
  const [facebookAppSecret, setFacebookAppSecret] = useState("");
  const [facebookConfigured, setFacebookConfigured] = useState(false);
  const [backendUrl, setBackendUrl] = useState("");
  const [frontendUrl, setFrontendUrl] = useState("");
  
  // Accordion states
  const [expandedVersion, setExpandedVersion] = useState(false);
  const [expandedUserCreation, setExpandedUserCreation] = useState(false);
  const [expandedSmtp, setExpandedSmtp] = useState(false);
  const [expandedMercadoPago, setExpandedMercadoPago] = useState(false);
  const [expandedAsaas, setExpandedAsaas] = useState(false);
  const [expandedUazapi, setExpandedUazapi] = useState(false);
  const [expandedFacebook, setExpandedFacebook] = useState(false);
  
  // Metrics states
  const [metrics, setMetrics] = useState({
    cpu: 0,
    memory: { used: 0, total: 0, percentage: 0 },
    disk: { used: 0, total: 0, percentage: 0 },
    uptime: 0,
  });

  useEffect(() => {
    fetchSystemVersion();
    fetchSmtpSettings();
    fetchUserCreation();
    fetchPaymentSettings();
    fetchUazapiSettings();
    fetchFacebookSettings();
    fetchMetrics();
    
    // Atualizar métricas a cada 5 segundos
    const metricsInterval = setInterval(fetchMetrics, 5000);
    
    // Pegar o backend URL do localStorage ou da variável de ambiente
    const backendURL = process.env.REACT_APP_BACKEND_URL || window.location.origin.replace(":3000", ":3001");
    const frontendURL = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
    setBackendUrl(backendURL);
    setFrontendUrl(frontendURL);
    
    return () => clearInterval(metricsInterval);
  }, []);

  const fetchSystemVersion = async () => {
    try {
      const { data } = await api.get("/settings");
      const versionSetting = data.find((s) => s.key === "systemVersion");
      if (versionSetting && versionSetting.value && versionSetting.value !== "1.0.0") {
        setVersion(versionSetting.value);
      } else {
        setVersion(packageJson.version);
      }
    } catch (error) {
      console.error("Erro ao buscar versão do sistema:", error);
      setVersion(packageJson.version);
    }
  };

  const fetchSmtpSettings = async () => {
    try {
      const { data } = await api.get("/settings");
      const settingsObj = {};

      data.forEach((setting) => {
        if (setting.key.startsWith("smtp")) {
          settingsObj[setting.key] = setting.value;
        }
      });

      setSmtpSettings((prev) => ({ ...prev, ...settingsObj }));
    } catch (error) {
      console.error("Erro ao buscar configurações SMTP:", error);
    }
  };

  const fetchUserCreation = async () => {
    try {
      const { data } = await api.get("/settings");
      const userCreationSetting = data.find((s) => s.key === "userCreation");
      if (userCreationSetting) {
        setUserCreation(userCreationSetting.value);
      }
    } catch (error) {
      console.error("Erro ao buscar configuração de cadastro:", error);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const { data } = await api.get("/settings");
      
      // Verificar Mercado Pago
      const mpSetting = data.find(s => s.key === "mptoken");
      if (mpSetting && mpSetting.value) {
        setMpToken(mpSetting.value);
        setMpConfigured(true);
      }
      
      // Verificar Asaas
      const asaasSetting = data.find(s => s.key === "asaastoken");
      if (asaasSetting && asaasSetting.value) {
        setAsaasToken(asaasSetting.value);
        setAsaasConfigured(true);
      }
    } catch (error) {
      console.error("Erro ao buscar configurações de pagamento:", error);
    }
  };

  const fetchUazapiSettings = async () => {
    try {
      const { data } = await api.get("/uazapi-settings");
      if (data) {
        setUazapiBaseUrl(data.baseUrl || "https://api.uazapi.com");
        if (data.adminToken) {
          setUazapiAdminToken(data.adminToken);
          setUazapiConfigured(true);
        }
      }
    } catch (error) {
      console.error("Erro ao buscar configurações UAZAPI:", error);
    }
  };

  const fetchFacebookSettings = async () => {
    try {
      const { data } = await api.get("/facebook-settings");
      if (data) {
        setFacebookVerifyToken(data.verifyToken || "whaticket");
        setFacebookAppId(data.appId || "");
        if (data.appSecret) {
          setFacebookAppSecret(data.appSecret);
          setFacebookConfigured(true);
        }
      }
    } catch (error) {
      console.error("Erro ao buscar configurações Facebook:", error);
    }
  };

  const fetchRestartConfig = async () => {
    // Removed - moved to Limits page
  };

  const fetchCleanupConfig = async () => {
    // Removed - moved to Limits page
  };

  const fetchMetrics = async () => {
    try {
      const { data } = await api.get("/server-metrics");
      if (data) {
        setMetrics(data);
      }
    } catch (error) {
      console.error("Erro ao buscar métricas do servidor:", error);
    }
  };

  const handleChangeUserCreation = async (value) => {
    setUserCreation(value);
    setLoadingUserCreation(true);
    try {
      await api.put("/settings/userCreation", { value });
      toast.success("✅ Configuração atualizada com sucesso!");
    } catch (error) {
      toast.error("Erro ao atualizar configuração: " + (error.response?.data?.error || error.message));
    } finally {
      setLoadingUserCreation(false);
    }
  };

  const handleSaveVersion = async () => {
    if (!version.trim()) {
      toast.error("Versão não pode estar vazia");
      return;
    }

    const versionRegex = /^\d+\.\d+\.\d+(-[a-zA-Z0-9.-]+)?$/;
    if (!versionRegex.test(version.trim())) {
      toast.warning("Use o formato semântico: MAJOR.MINOR.PATCH (ex: 1.0.0)");
    }

    setLoading(true);
    try {
      await api.put("/settings/systemVersion", { value: version.trim() });
      toast.success("✅ Versão do sistema atualizada com sucesso!");
      await fetchSystemVersion();
    } catch (error) {
      toast.error("Erro ao atualizar versão: " + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSmtp = async () => {
    setLoading(true);
    try {
      for (const [key, value] of Object.entries(smtpSettings)) {
        await api.put(`/settings/${key}`, { value });
      }

      toast.success("✅ Configurações SMTP salvas com sucesso!");
      await fetchSmtpSettings();
    } catch (error) {
      toast.error("Erro ao salvar configurações SMTP: " + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSmtpChange = (key, value) => {
    setSmtpSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveMercadoPago = async () => {
    if (!mpToken.trim()) {
      toast.error("Access Token é obrigatório");
      return;
    }

    setLoading(true);
    try {
      await api.put("/settings/mptoken", { value: mpToken.trim() });
      toast.success("✅ Mercado Pago configurado com sucesso!");
      setMpConfigured(true);
    } catch (error) {
      toast.error("Erro ao configurar Mercado Pago: " + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAsaas = async () => {
    if (!asaasToken.trim()) {
      toast.error("Token é obrigatório");
      return;
    }

    setLoading(true);
    try {
      await api.put("/settings/asaastoken", { value: asaasToken.trim() });
      toast.success("✅ Asaas configurado com sucesso!");
      setAsaasConfigured(true);
    } catch (error) {
      toast.error("Erro ao configurar Asaas: " + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUazapi = async () => {
    if (!uazapiAdminToken.trim()) {
      toast.error("Admin Token é obrigatório");
      return;
    }

    setLoading(true);
    try {
      await api.put("/uazapi-settings", { 
        baseUrl: uazapiBaseUrl,
        adminToken: uazapiAdminToken.trim() 
      });
      toast.success("✅ UAZAPI configurado com sucesso!");
      setUazapiConfigured(true);
    } catch (error) {
      toast.error("Erro ao configurar UAZAPI: " + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFacebook = async () => {
    if (!facebookAppId.trim() || !facebookAppSecret.trim()) {
      toast.error("App ID e App Secret são obrigatórios");
      return;
    }

    setLoading(true);
    try {
      await api.put("/facebook-settings", { 
        verifyToken: facebookVerifyToken.trim(),
        appId: facebookAppId.trim(),
        appSecret: facebookAppSecret.trim()
      });
      toast.success("✅ Facebook & Instagram configurado com sucesso!");
      setFacebookConfigured(true);
    } catch (error) {
      toast.error("Erro ao configurar Facebook: " + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyWebhookUrl = () => {
    const webhookUrl = `${backendUrl}/webhook`;
    navigator.clipboard.writeText(webhookUrl);
    toast.success("✅ URL do webhook copiada para a área de transferência!");
  };

  const handleCopyBackendUrl = () => {
    navigator.clipboard.writeText(backendUrl);
    toast.success("✅ URL do backend copiada para a área de transferência!");
  };

  const handleCopyFrontendUrl = () => {
    navigator.clipboard.writeText(frontendUrl);
    toast.success("✅ URL do frontend copiada para a área de transferência!");
  };

  const handleSaveRestartConfig = async () => {
    // Removed - moved to Limits page
  };

  const handleRestartBackend = async () => {
    // Removed - moved to Limits page
  };

  const handleRestartFrontend = () => {
    // Removed - moved to Limits page
  };

  const handleRestartVPS = async () => {
    // Removed - moved to Limits page
  };

  const handleToggleCleanup = async (enabled) => {
    // Removed - moved to Limits page
  };

  const handleSaveCleanupConfig = async () => {
    // Removed - moved to Limits page
  };

  const handleRunCleanupNow = async () => {
    // Removed - moved to Limits page
  };

  return (
    <MainContainer>
      <div className={classes.container}>
        {/* Header */}
        <div className={classes.header}>
          <h1 className={classes.title}>⚙️ Configurações do Sistema</h1>
        </div>

        {/* Server Metrics */}
        <div className={classes.sectionCard}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
            <StorageIcon className={classes.sectionIcon} />
            <span className={classes.sectionTitle}>Métricas do Servidor (Tempo Real)</span>
          </div>

          <Grid container spacing={3}>
            {/* CPU */}
            <Grid item xs={12} md={4}>
              <div style={{ padding: "12px", background: "#f9fafb", borderRadius: "8px" }}>
                <div style={{ fontSize: "12px", fontWeight: 600, color: "#666", marginBottom: "8px" }}>
                  CPU
                </div>
                <div style={{ fontSize: "20px", fontWeight: 700, color: "#3b82f6", marginBottom: "8px" }}>
                  {metrics?.cpu ? metrics.cpu.toFixed(1) : "0"}%
                </div>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(metrics?.cpu || 0, 100)}
                  style={{ height: "8px", borderRadius: "4px" }}
                />
              </div>
            </Grid>

            {/* Memory */}
            <Grid item xs={12} md={4}>
              <div style={{ padding: "12px", background: "#f9fafb", borderRadius: "8px" }}>
                <div style={{ fontSize: "12px", fontWeight: 600, color: "#666", marginBottom: "8px" }}>
                  Memória
                </div>
                <div style={{ fontSize: "20px", fontWeight: 700, color: "#10b981", marginBottom: "8px" }}>
                  {metrics?.memory?.percentage ? metrics.memory.percentage.toFixed(1) : "0"}%
                </div>
                <LinearProgress 
                  variant="determinate" 
                  value={metrics?.memory?.percentage || 0}
                  style={{ height: "8px", borderRadius: "4px" }}
                />
              </div>
            </Grid>

            {/* Disk */}
            <Grid item xs={12} md={4}>
              <div style={{ padding: "12px", background: "#f9fafb", borderRadius: "8px" }}>
                <div style={{ fontSize: "12px", fontWeight: 600, color: "#666", marginBottom: "8px" }}>
                  Disco (public)
                </div>
                <div style={{ fontSize: "20px", fontWeight: 700, color: "#f59e0b", marginBottom: "8px" }}>
                  {metrics?.disk?.percentage ? metrics.disk.percentage.toFixed(1) : "0"}%
                </div>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(metrics?.disk?.percentage || 0, 100)}
                  style={{ height: "8px", borderRadius: "4px" }}
                />
              </div>
            </Grid>

            {/* Uptime */}
            <Grid item xs={12}>
              <div style={{ padding: "12px", background: "#f9fafb", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "12px", fontWeight: 600, color: "#666", marginBottom: "4px" }}>
                  Tempo de Atividade
                </div>
                <div style={{ fontSize: "18px", fontWeight: 700, color: "#8b5cf6" }}>
                  {metrics?.uptime ? `${Math.floor(metrics.uptime / 86400)}d ${Math.floor((metrics.uptime % 86400) / 3600)}h ${Math.floor((metrics.uptime % 3600) / 60)}m` : "0m"}
                </div>
              </div>
            </Grid>
          </Grid>
        </div>

        {/* Versão do Sistema */}
        <div className={classes.sectionCard}>
          <div 
            className={classes.sectionHeader}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <SettingsIcon className={classes.sectionIcon} />
              <span className={classes.sectionTitle}>Versão do Sistema</span>
            </div>
            <span style={{ 
              fontSize: "16px", 
              fontWeight: 600, 
              color: theme.palette.primary.main,
              backgroundColor: `${theme.palette.primary.main}10`,
              padding: "6px 16px",
              borderRadius: "8px",
            }}>
              v{packageJson.version}
            </span>
          </div>
        </div>

        {/* Permitir Cadastros */}
        <div className={classes.sectionCard}>
          <div 
            className={classes.sectionHeaderClickable}
            onClick={() => setExpandedUserCreation(!expandedUserCreation)}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <CheckCircleIcon className={classes.sectionIcon} />
              <span className={classes.sectionTitle}>Configurações de Cadastro</span>
            </div>
            <ExpandMoreIcon 
              className={`${classes.expandIcon} ${expandedUserCreation ? classes.expandIconOpen : ""}`}
            />
          </div>

          <Collapse in={expandedUserCreation}>
            <p className={classes.helpText} style={{ marginBottom: "16px" }}>
              Controle se novos usuários podem se cadastrar no sistema
            </p>

            <div className={classes.switchContainer}>
              <FormControlLabel
                control={
                  <Switch
                    checked={userCreation === "enabled"}
                    onChange={(e) => handleChangeUserCreation(e.target.checked ? "enabled" : "disabled")}
                    disabled={loadingUserCreation}
                    color="primary"
                  />
                }
                label="Permitir Cadastros"
              />
            </div>
          </Collapse>
        </div>

        {/* Configuração SMTP */}
        <div className={classes.sectionCard}>
          <div 
            className={classes.sectionHeaderClickable}
            onClick={() => setExpandedSmtp(!expandedSmtp)}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <EmailIcon className={classes.sectionIcon} />
              <span className={classes.sectionTitle}>Configurações SMTP</span>
            </div>
            <ExpandMoreIcon 
              className={`${classes.expandIcon} ${expandedSmtp ? classes.expandIconOpen : ""}`}
            />
          </div>

          <Collapse in={expandedSmtp}>
            <p className={classes.helpText} style={{ marginBottom: "16px" }}>
              Configure o servidor SMTP para envio de emails de recuperação de senha
            </p>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>Servidor SMTP</label>
                  <TextField
                    fullWidth
                    type="text"
                    value={smtpSettings.smtpHost}
                    onChange={(e) => handleSmtpChange("smtpHost", e.target.value)}
                    placeholder="smtp.gmail.com"
                    variant="outlined"
                    size="small"
                  />
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>Porta</label>
                  <TextField
                    fullWidth
                    type="number"
                    value={smtpSettings.smtpPort}
                    onChange={(e) => handleSmtpChange("smtpPort", e.target.value)}
                    placeholder="465"
                    variant="outlined"
                    size="small"
                  />
                </div>
              </Grid>

              <Grid item xs={12}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>Email (Usuário)</label>
                  <TextField
                    fullWidth
                    type="email"
                    value={smtpSettings.smtpUser}
                    onChange={(e) => handleSmtpChange("smtpUser", e.target.value)}
                    placeholder="seuemail@gmail.com"
                    variant="outlined"
                    size="small"
                  />
                </div>
              </Grid>

              <Grid item xs={12}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>Senha</label>
                  <TextField
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    value={smtpSettings.smtpPass}
                    onChange={(e) => handleSmtpChange("smtpPass", e.target.value)}
                    placeholder="Senha do email"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <button
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            display: "flex",
                            alignItems: "center",
                            color: "#666",
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </button>
                      ),
                    }}
                  />
                </div>
              </Grid>

              <Grid item xs={12}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>Email de Envio (From)</label>
                  <TextField
                    fullWidth
                    type="email"
                    value={smtpSettings.smtpFrom}
                    onChange={(e) => handleSmtpChange("smtpFrom", e.target.value)}
                    placeholder="noreply@seudominio.com"
                    variant="outlined"
                    size="small"
                  />
                </div>
              </Grid>

              <Grid item xs={12}>
                <div className={classes.switchContainer}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={smtpSettings.smtpSecure === "true"}
                        onChange={(e) => handleSmtpChange("smtpSecure", e.target.checked ? "true" : "false")}
                        color="primary"
                      />
                    }
                    label="Usar conexão segura (SSL/TLS)"
                  />
                </div>
              </Grid>
            </Grid>

            <div className={classes.infoBox}>
              <div style={{ fontSize: "13px", fontWeight: 600, color: "#0369a1", marginBottom: "8px" }}>
                ℹ️ Informações Importantes
              </div>
              <div className={classes.infoBoxText}>
                • Para Gmail, você precisa gerar uma "Senha de App" nas configurações de segurança<br />
                • Porta 465 usa SSL, porta 587 usa TLS<br />
                • O email de envio (From) será usado como remetente dos emails de recuperação de senha
              </div>
            </div>

            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveSmtp}
              disabled={loading}
              className={classes.saveButton}
            >
              {loading ? "Salvando..." : "Salvar Configurações SMTP"}
            </Button>
          </Collapse>
        </div>

        {/* Configuração Mercado Pago */}
        <div className={classes.sectionCard}>
          <div 
            className={classes.sectionHeaderClickable}
            onClick={() => setExpandedMercadoPago(!expandedMercadoPago)}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <PaymentIcon className={classes.sectionIcon} />
              <span className={classes.sectionTitle}>Mercado Pago</span>
            </div>
            <ExpandMoreIcon 
              className={`${classes.expandIcon} ${expandedMercadoPago ? classes.expandIconOpen : ""}`}
            />
          </div>

          <Collapse in={expandedMercadoPago}>
            <p className={classes.helpText} style={{ marginBottom: "16px" }}>
              Configure sua integração com Mercado Pago para receber pagamentos
            </p>

            {mpConfigured && (
              <div className={classes.infoBox} style={{ background: "#d1fae5", borderColor: "#a7f3d0" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#065f46", marginBottom: "4px" }}>
                  ✓ Mercado Pago Configurado
                </div>
                <div style={{ fontSize: "12px", color: "#047857" }}>
                  Sua integração está ativa e pronta para receber pagamentos
                </div>
              </div>
            )}

            <Grid container spacing={2} style={{ marginTop: mpConfigured ? "16px" : "0" }}>
              <Grid item xs={12}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>Access Token</label>
                  <TextField
                    fullWidth
                    type="password"
                    value={mpToken}
                    onChange={(e) => setMpToken(e.target.value)}
                    placeholder="APP_USR-..."
                    variant="outlined"
                    size="small"
                  />
                  <div className={classes.helpText}>
                    Obtenha em: <a href="https://www.mercadopago.com.br/developers/panel/credentials" target="_blank" rel="noopener noreferrer" style={{ color: "#00b1ea" }}>Credenciais de produção</a> no Mercado Pago
                  </div>
                </div>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveMercadoPago}
              disabled={loading}
              className={classes.saveButton}
            >
              {loading ? "Salvando..." : "Salvar Mercado Pago"}
            </Button>
          </Collapse>
        </div>

        {/* Configuração Asaas */}
        <div className={classes.sectionCard}>
          <div 
            className={classes.sectionHeaderClickable}
            onClick={() => setExpandedAsaas(!expandedAsaas)}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <PaymentIcon className={classes.sectionIcon} />
              <span className={classes.sectionTitle}>Asaas</span>
            </div>
            <ExpandMoreIcon 
              className={`${classes.expandIcon} ${expandedAsaas ? classes.expandIconOpen : ""}`}
            />
          </div>

          <Collapse in={expandedAsaas}>
            <p className={classes.helpText} style={{ marginBottom: "16px" }}>
              Configure sua integração com Asaas para gestão de cobranças e faturas
            </p>

            {asaasConfigured && (
              <div className={classes.infoBox} style={{ background: "#d1fae5", borderColor: "#a7f3d0" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#065f46", marginBottom: "4px" }}>
                  ✓ Asaas Configurado
                </div>
                <div style={{ fontSize: "12px", color: "#047857" }}>
                  Sua integração está ativa e pronta para gerenciar cobranças
                </div>
              </div>
            )}

            <Grid container spacing={2} style={{ marginTop: asaasConfigured ? "16px" : "0" }}>
              <Grid item xs={12}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>API Token</label>
                  <TextField
                    fullWidth
                    type="password"
                    value={asaasToken}
                    onChange={(e) => setAsaasToken(e.target.value)}
                    placeholder="$aact_..."
                    variant="outlined"
                    size="small"
                  />
                  <div className={classes.helpText}>
                    Obtenha em: <a href="https://www.asaas.com/config/api" target="_blank" rel="noopener noreferrer" style={{ color: "#00a859" }}>Integrações &gt; API</a> no painel Asaas
                  </div>
                </div>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveAsaas}
              disabled={loading}
              className={classes.saveButton}
            >
              {loading ? "Salvando..." : "Salvar Asaas"}
            </Button>
          </Collapse>
        </div>

        {/* Configuração UAZAPI */}
        <div className={classes.sectionCard}>
          <div 
            className={classes.sectionHeaderClickable}
            onClick={() => setExpandedUazapi(!expandedUazapi)}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <ApiIcon className={classes.sectionIcon} />
              <span className={classes.sectionTitle}>UAZAPI</span>
            </div>
            <ExpandMoreIcon 
              className={`${classes.expandIcon} ${expandedUazapi ? classes.expandIconOpen : ""}`}
            />
          </div>

          <Collapse in={expandedUazapi}>
            <p className={classes.helpText} style={{ marginBottom: "16px" }}>
              Configure sua integração com UAZAPI para automação de WhatsApp
            </p>

            {uazapiConfigured && (
              <div className={classes.infoBox} style={{ background: "#d1fae5", borderColor: "#a7f3d0" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#065f46", marginBottom: "4px" }}>
                  ✓ UAZAPI Configurado
                </div>
                <div style={{ fontSize: "12px", color: "#047857" }}>
                  Sua integração está ativa e pronta para automação
                </div>
              </div>
            )}

            <Grid container spacing={2} style={{ marginTop: uazapiConfigured ? "16px" : "0" }}>
              <Grid item xs={12} md={6}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>Base URL</label>
                  <TextField
                    fullWidth
                    type="text"
                    value={uazapiBaseUrl}
                    onChange={(e) => setUazapiBaseUrl(e.target.value)}
                    placeholder="https://api.uazapi.com"
                    variant="outlined"
                    size="small"
                  />
                  <div className={classes.helpText}>
                    URL base da API UAZAPI
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>Admin Token</label>
                  <TextField
                    fullWidth
                    type="password"
                    value={uazapiAdminToken}
                    onChange={(e) => setUazapiAdminToken(e.target.value)}
                    placeholder="Seu token de administrador"
                    variant="outlined"
                    size="small"
                  />
                  <div className={classes.helpText}>
                    Token de administrador para autenticação na API
                  </div>
                </div>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveUazapi}
              disabled={loading}
              className={classes.saveButton}
            >
              {loading ? "Salvando..." : "Salvar UAZAPI"}
            </Button>
          </Collapse>
        </div>

        {/* Configuração Facebook & Instagram */}
        <div className={classes.sectionCard}>
          <div 
            className={classes.sectionHeaderClickable}
            onClick={() => setExpandedFacebook(!expandedFacebook)}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <FacebookIcon className={classes.sectionIcon} />
              <span className={classes.sectionTitle}>Facebook & Instagram</span>
            </div>
            <ExpandMoreIcon 
              className={`${classes.expandIcon} ${expandedFacebook ? classes.expandIconOpen : ""}`}
            />
          </div>

          <Collapse in={expandedFacebook}>
            <p className={classes.helpText} style={{ marginBottom: "16px" }}>
              Configure sua integração com Facebook e Instagram para atendimento
            </p>

            {facebookConfigured && (
              <div className={classes.infoBox} style={{ background: "#d1fae5", borderColor: "#a7f3d0" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#065f46", marginBottom: "4px" }}>
                  ✓ Facebook & Instagram Configurado
                </div>
                <div style={{ fontSize: "12px", color: "#047857" }}>
                  Sua integração está ativa e pronta para atendimento
                </div>
              </div>
            )}

            <Grid container spacing={2} style={{ marginTop: facebookConfigured ? "16px" : "0" }}>
              <Grid item xs={12} md={6}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>Verify Token</label>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <TextField
                      fullWidth
                      type="text"
                      value={facebookVerifyToken}
                      variant="outlined"
                      size="small"
                      disabled
                      style={{ background: "#f9fafb", cursor: "not-allowed" }}
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(facebookVerifyToken);
                        toast.success("✅ Verify Token copiado para a área de transferência!");
                      }}
                      style={{
                        padding: "10px 16px",
                        background: "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontWeight: 600,
                        fontSize: "13px",
                        transition: "all 0.3s ease",
                        whiteSpace: "nowrap",
                      }}
                      onMouseEnter={(e) => e.target.style.background = "#2563eb"}
                      onMouseLeave={(e) => e.target.style.background = "#3b82f6"}
                    >
                      <CopyIcon style={{ fontSize: "16px" }} />
                      Copiar
                    </button>
                  </div>
                  <div className={classes.helpText}>
                    Token de verificação para webhook do Facebook
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>App ID</label>
                  <TextField
                    fullWidth
                    type="text"
                    value={facebookAppId}
                    onChange={(e) => setFacebookAppId(e.target.value)}
                    placeholder="Seu App ID do Facebook"
                    variant="outlined"
                    size="small"
                  />
                  <div className={classes.helpText}>
                    ID do aplicativo criado no Facebook Developers
                  </div>
                </div>
              </Grid>

              <Grid item xs={12}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>App Secret</label>
                  <TextField
                    fullWidth
                    type="password"
                    value={facebookAppSecret}
                    onChange={(e) => setFacebookAppSecret(e.target.value)}
                    placeholder="Seu App Secret do Facebook"
                    variant="outlined"
                    size="small"
                  />
                  <div className={classes.helpText}>
                    Chave secreta do aplicativo Facebook
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>API Backend</label>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <TextField
                      fullWidth
                      type="text"
                      value={backendUrl}
                      variant="outlined"
                      size="small"
                      disabled
                      style={{ background: "#f9fafb", cursor: "not-allowed" }}
                    />
                    <button
                      onClick={handleCopyBackendUrl}
                      style={{
                        padding: "10px 16px",
                        background: "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontWeight: 600,
                        fontSize: "13px",
                        transition: "all 0.3s ease",
                        whiteSpace: "nowrap",
                      }}
                      onMouseEnter={(e) => e.target.style.background = "#2563eb"}
                      onMouseLeave={(e) => e.target.style.background = "#3b82f6"}
                    >
                      <CopyIcon style={{ fontSize: "16px" }} />
                      Copiar
                    </button>
                  </div>
                  <div className={classes.helpText}>
                    URL da API Backend
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>API Frontend</label>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <TextField
                      fullWidth
                      type="text"
                      value={frontendUrl}
                      variant="outlined"
                      size="small"
                      disabled
                      style={{ background: "#f9fafb", cursor: "not-allowed" }}
                    />
                    <button
                      onClick={handleCopyFrontendUrl}
                      style={{
                        padding: "10px 16px",
                        background: "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontWeight: 600,
                        fontSize: "13px",
                        transition: "all 0.3s ease",
                        whiteSpace: "nowrap",
                      }}
                      onMouseEnter={(e) => e.target.style.background = "#2563eb"}
                      onMouseLeave={(e) => e.target.style.background = "#3b82f6"}
                    >
                      <CopyIcon style={{ fontSize: "16px" }} />
                      Copiar
                    </button>
                  </div>
                  <div className={classes.helpText}>
                    URL da API Frontend
                  </div>
                </div>
              </Grid>

              <Grid item xs={12}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>Webhook URL</label>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <TextField
                      fullWidth
                      type="text"
                      value={`${backendUrl}/webhook`}
                      variant="outlined"
                      size="small"
                      disabled
                      style={{ background: "#f9fafb", cursor: "not-allowed" }}
                    />
                    <button
                      onClick={handleCopyWebhookUrl}
                      style={{
                        padding: "10px 16px",
                        background: "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontWeight: 600,
                        fontSize: "13px",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => e.target.style.background = "#2563eb"}
                      onMouseLeave={(e) => e.target.style.background = "#3b82f6"}
                    >
                      <CopyIcon style={{ fontSize: "16px" }} />
                      Copiar
                    </button>
                  </div>
                  <div className={classes.helpText}>
                    Cole esta URL nas configurações de webhook do Facebook Developers
                  </div>
                </div>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveFacebook}
              disabled={loading}
              className={classes.saveButton}
            >
              {loading ? "Salvando..." : "Salvar Facebook & Instagram"}
            </Button>
          </Collapse>
        </div>

        {/* Limites do Sistema */}
        <div className={classes.sectionCard}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <StorageIcon className={classes.sectionIcon} />
              <span className={classes.sectionTitle}>Limites do Sistema</span>
            </div>
            <Link to="/settings/system/limits" style={{ textDecoration: "none" }}>
              <Button
                variant="contained"
                color="primary"
                style={{ textTransform: "none", fontWeight: 600 }}
              >
                Gerenciar Limites
              </Button>
            </Link>
          </div>
          <p className={classes.helpText} style={{ marginTop: "12px" }}>
            Configure o limite máximo de empresas, gerenciamento do sistema e limpeza de arquivos
          </p>
        </div>
      </div>
    </MainContainer>
  );
};

export default SystemSettings;
